import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { forwardRef, Fragment, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { longStringPreview } from '../utils/formatter'

import { twMerge } from 'tailwind-merge'
import { Token } from '../api'
import assets from '../assets'
import external_urls from '../external_urls'
import { TopGuildRoleIcon } from './Roles'

type CollectionViewCardProps = {
    token: Token
}

export const CollectionViewCard = forwardRef(({ token }: CollectionViewCardProps, ref: any) => {
    const navigate = useNavigate()
    const [fliped, setFliped] = useState(false)
    const [addressCopied, setAddressCopied] = useState(false)

    const copyClipboard = (e, value) => {
        e.stopPropagation()
        setAddressCopied(true)
        navigator.clipboard.writeText(value).then(() => {
            setTimeout(() => setAddressCopied(false), 500)
        })
    }

    return (
        <div
            ref={ref}
            style={{
                background: token.is_legendary
                    ? 'linear-gradient(180deg,rgba(255,214,0,0.25) 0%,rgba(175,147,0,0.0625) 117.36%)'
                    : 'linear-gradient(180deg, rgba(255, 255, 255, 0.075) 0%, rgba(255, 255, 255, 0) 117.36%)',
            }}
            className={`flex h-max flex-col overflow-hidden backdrop-blur-sm border border-[rgba(255,255,255,0.25)] rounded mx-auto md:w-auto`}
        >
            <div className='flex flex-row gap-x-1 py-3 px-4 text-sm justify-center items-center relative w-full font-bold'>
                <div className='flex-1 text-[0.6rem] uppercase whitespace-nowrap'>
                    {token.is_holder && (
                        <FontAwesomeIcon icon={faCheck} className='text-green-400 px-1'></FontAwesomeIcon>
                    )}
                    <span>{token.name}</span>
                </div>
                <span
                    title='Rarity Rank'
                    className={`px-2 min-w-min rounded-xl text-center text-[0.5rem] bg-[#9251FF] leading-relaxed`}
                >
                    {token.rank}
                </span>
                <img
                    title='Show Attributes'
                    src={assets.swap_icon}
                    className='cursor-pointer ml-4'
                    onClick={() => setFliped((prev) => !prev)}
                />
            </div>

            <div className='flex-1 flex flex-col'>
                <div className='relative flex flex-row w-full perspective rounded'>
                    {/* Card Front Side */}
                    <div
                        className={`transition-transform duration-500 cursor-pointer w-full aspect-square bg-black ${
                            fliped ? '-rotate-flip' : 'no-flip'
                        }`}
                        onClick={() => navigate(`/operative/${token.operative_id}`)}
                    >
                        <NFTImage token={token} />
                    </div>
                    {token.is_legendary && !fliped && (
                        <img src={assets.star} title='Legendary' className='absolute right-2 top-3 h-5 w-5' />
                    )}
                    {/* Card Back Side */}
                    <div
                        style={{
                            background:
                                'linear-gradient(221.43deg, rgba(0, 0, 0, 0.4) -6.16%, rgba(32, 106, 152, 0.4) 123.04%)',
                        }}
                        className={`absolute top-0 left-0 transition-transform duration-500 h-full w-full overflow-x-hidden grid grid-cols-2 gap-2 text-xs font-bold p-2 scrollbar-tiny scroll-smooth ${
                            !fliped
                                ? 'rotate-flip overflow-y-hidden border-r-[12px] border-transparent'
                                : 'no-flip overflow-y-auto'
                        }`}
                    >
                        {token.traits.map((a, i) => (
                            <Fragment key={i}>
                                <div>{a.type}</div>
                                <div className='text-right'>{a.value}</div>
                            </Fragment>
                        ))}
                    </div>
                </div>
                <div className='flex flex-col gap-2 items-center w-full h-full text-xs'>
                    <div className='flex flex-row gap-x-4 mt-2 items-center justify-center'>
                        {(
                            [
                                [external_urls.solana_fm, assets.solanafm_icon],
                                [external_urls.solscan, assets.solscan_icon],
                                [external_urls.solana_explorer, assets.solanaexplorer_icon],
                                [external_urls.magiceden_nft, assets.magiceden_icon],
                                [external_urls.opensea_nft, assets.opensea_icon],
                                [external_urls.fractal_nft, assets.fractal_icon],
                            ] as Array<[(string) => string, string]>
                        ).map(([url, icon], i) => (
                            <a key={i} target='blank' href={url(token)}>
                                <img src={icon} className='h-4 rounded' />
                            </a>
                        ))}
                    </div>

                    <div className='grid grid-cols-3 w-full px-4 gap-y-4 pt-4 items-start font-bold text-[0.65rem]'>
                        <span className='text-xs'>MINT:</span>
                        <div
                            className='col-span-2 cursor-pointer opacity-50 hover:opacity-100 text-right'
                            title={token.mint}
                            onClick={(e) => copyClipboard(e, token.mint)}
                        >
                            {addressCopied ? 'copied' : longStringPreview(token.mint)}
                        </div>

                        <span className='text-xs'>HOLDER:</span>
                        <div className='col-span-2 flex flex-row flex-wrap gap-2 justify-end'>
                            <div
                                title={token.holder_wallet}
                                className={`flex flex-row items-center gap-1 text-right cursor-pointer`}
                            >
                                <div className='h-4'>
                                    <TopGuildRoleIcon roles={token.holder_roles} />
                                </div>
                                <span className='opacity-50 hover:opacity-100'>
                                    {token.holder?.length > 16 ? longStringPreview(token.holder) : token.holder}
                                </span>
                            </div>
                            {(token.holder_username || token.holder_discord || token.holder_twitter) && (
                                <div className='flex flex-row gap-2'>
                                    {token.holder_username && (
                                        <img src={assets.wallet_icon} title={token.holder_wallet} className='h-4 w-4' />
                                    )}
                                    {token.holder_discord && (
                                        <img
                                            src={assets.discord_icon}
                                            title={token.holder_discord}
                                            className='h-4 w-4'
                                        />
                                    )}
                                    {token.holder_twitter && (
                                        <a href={`https://twitter.com/${token.holder_twitter}`} target='blank'>
                                            <img
                                                src={assets.twitter_icon}
                                                title={token.holder_twitter}
                                                className='h-4 w-4'
                                            />
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                        <div></div>
                    </div>

                    {token.price && (
                        <div
                            style={{
                                background: token.is_legendary ? 'rgba(255, 214, 0, 0.25)' : 'rgba(255, 255, 255, 0.1)',
                            }}
                            className='flex flex-row py-1 px-3 items-center justify-between w-full h-10'
                        >
                            <Fragment>
                                <div className='flex items-center justify-center'>
                                    <img className='inline w-2.5 h-2.5 mx-1' src={assets.sol_symbol} />
                                    <span className='text-sm font-bold'>{token.price}</span>
                                </div>
                                <a
                                    className='button-default hover:scale-[102%] duration-75 rounded-md w-max px-10 py-1'
                                    href={`https://magiceden.io/item-details/${token.mint}`}
                                    target='_blank'
                                >
                                    BUY
                                </a>
                            </Fragment>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
})

const skullIcon = new URL('~/src/assets/favicon/android-chrome-192x192.png', import.meta.url).href
const NFTImage = ({ token }) => {
    const [loaded, setLoaded] = useState(false)

    const onLoad = () => {
        setLoaded(true)
    }

    useEffect(() => setLoaded(false), [token])

    return (
        <div className={twMerge('relative overflow-hidden', !loaded && 'bg-white/5 animate-pulse')}>
            {!loaded && (
                <img
                    src={skullIcon}
                    className={twMerge(
                        'h-20 w-20 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-10'
                    )}
                />
            )}
            <img src={token.image_url} loading='lazy' onLoad={onLoad} className={twMerge(!loaded && 'opacity-0')} />
        </div>
    )
}
